import { ArrowDown01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { Checkbox, CheckboxSize, Show } from "shared/components";
import { Card } from "shared/components/Card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "shared/components/Popover";
import { cx } from "shared/utils";
import {
  SIMILAR_UNIT_STAGE_FILTER_OPTIONS,
  type SimilarUnitStage
} from "shared/app/SimilarUnit/constants";
import {
  getSimilarUnitChipLabel,
  toggleSimilarUnitStage
} from "shared/app/SimilarUnit/stageFilter";

const NOOP = () => {};

/**
 * "Similar unit: …" control beside Tags. Shares `similar_unit_stages` with the
 * Filters drawer section; several stages show leads at any of them.
 */
export const SimilarUnitFilterPopover = ({
  open,
  onOpenChange,
  selectedStages,
  onSelectedStagesChange
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedStages: SimilarUnitStage[];
  onSelectedStagesChange: (stages: SimilarUnitStage[]) => void;
}) => {
  const hasSelection = selectedStages.length > 0;

  return (
    <Popover open={open} onOpenChange={onOpenChange} placement="bottom-start">
      <PopoverTrigger onClick={() => onOpenChange(true)}>
        <Card
          className={cx(
            "w-fit max-w-[314px] px-4 py-2 flex items-center gap-2 min-h-[40px] h-full shadow-xs cursor-pointer",
            hasSelection && "!border-primary-500 !bg-primary-20"
          )}
        >
          <span className="text-content-200 font-bold text-xs shrink-0">
            Similar unit:
          </span>
          <span className="font-bold text-content-1000 text-xs truncate">
            {getSimilarUnitChipLabel(selectedStages)}
          </span>
          {hasSelection ? (
            <button
              type="button"
              aria-label="Clear Similar unit filter"
              className="shrink-0 px-1 text-base leading-none text-content-300 hover:text-content-1000"
              onClick={(event) => {
                event.stopPropagation();
                onSelectedStagesChange([]);
                onOpenChange(false);
              }}
            >
              ×
            </button>
          ) : (
            <HugeiconsIcon
              icon={ArrowDown01Icon}
              size={16}
              className={cx(
                "text-content-1000 shrink-0",
                open ? "rotate-180" : ""
              )}
            />
          )}
        </Card>
      </PopoverTrigger>
      <PopoverContent className="max-w-[240px] w-full p-0 rounded-lg border border-alpha-100 shadow-lg bg-content-1000-inverted z-10">
        <div className="py-1">
          {SIMILAR_UNIT_STAGE_FILTER_OPTIONS.map((option) => {
            const checked = selectedStages.includes(option.id);
            return (
              <div
                key={option.id}
                role="option"
                aria-selected={checked}
                className="flex w-full items-center gap-3 px-4 py-2.5 hover:bg-alpha-40 cursor-pointer"
                onClick={() =>
                  onSelectedStagesChange(
                    toggleSimilarUnitStage(selectedStages, option.id)
                  )
                }
              >
                <Checkbox
                  checked={checked}
                  size={CheckboxSize.SM}
                  wrapperClassName="!w-auto !flex-none !p-0 justify-center"
                  onChange={NOOP}
                />
                <span className="truncate text-sm font-medium text-content-1000">
                  {option.name}
                </span>
              </div>
            );
          })}
        </div>
        <Show if={hasSelection}>
          <p className="border-t border-alpha-40 bg-alpha-20 px-4 py-2 text-xs text-content-400">
            Shows leads at any selected stage
          </p>
        </Show>
      </PopoverContent>
    </Popover>
  );
};
